/**
 * Term-sheet pre-fill from the deal record (MOD-01).
 *
 * The builder opened every live deal on one boilerplate: "24 Months", "SOFR +
 * 350 (1.00% floor)", "Greater of 1.0% or Yield Maintenance", "Acquisition" —
 * contradicting the deal's own stored terms (The Marlowe stores a 36-month
 * term, a 3.00% floor and "18 months minimum interest, open thereafter"). And
 * it read the loan amount — DOLLARS on a live loan — as millions, so the
 * letter offered a $24.5-trillion loan and a $122.5-billion deposit.
 *
 * `termsFromDeal` returns only what the deal record states. Every term it does
 * not return stays a platform default, and the builder labels those
 * "Suggested · no source" until a person confirms them.
 */
import { amountUnitOf, toMillions } from '@/pages/liveModuleData'

export interface DealTermPrefill {
  loanAmount?: number
  transactionType?: string
  term?: string
  spreadBps?: number
  rateIndex?: string
  rateFloorPct?: string
  fixedRatePct?: string
  ioPeriod?: string
  amortization?: string
  extension?: string
  prepaymentFee?: string
  origFeePct?: string
  exitFeePct?: string
  recourse?: string
  security?: string
  brokerName?: string
  brokerFirm?: string
  brokerPhone?: string
}

export interface TermSheetDealLike {
  loanAmount: number
  /** Absent means dollars (the loans API); the curated demo book declares $MM. */
  amountUnit?: 'USD' | 'USD_MM'
  rate?: string | null
  brokerName?: string | null
  customFields?: Record<string, unknown> | null
  /** Terms a later email states, held for a person to confirm (R5/R6). */
  pendingRevisions?: { field: string; incoming: unknown; source?: string; subject?: string; receivedAt?: string | null }[] | null
}

export interface PendingTerm {
  value: string
  source?: string
}

/**
 * The builder fields a pending revision would fill, with the value it states.
 * The Marlowe's A02 term sheet (3.00% floor, 18 months minimum interest) waits
 * for review; the builder labelled those fields "Suggested · no source".
 */
export function pendingTermsFromDeal(deal: TermSheetDealLike): Partial<Record<keyof DealTermPrefill, PendingTerm>> {
  const out: Partial<Record<keyof DealTermPrefill, PendingTerm>> = {}
  for (const rev of deal.pendingRevisions ?? []) {
    if (!rev || typeof rev.field !== 'string') continue
    const terms = termsFromDeal({ loanAmount: 0, customFields: { [rev.field]: rev.incoming } })
    for (const [key, value] of Object.entries(terms) as [keyof DealTermPrefill, unknown][]) {
      if (key === 'loanAmount' || value == null || key in out) continue
      out[key] = { value: String(value), source: rev.source ?? rev.subject ?? undefined }
    }
  }
  return out
}

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return null
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null
}

function pct(n: number): string {
  return `${n.toFixed(2)}%`
}

/**
 * A stored rate as a PERCENT, whichever unit it was stored in: 0.0325
 * (fraction — the seed, the backend) and 3.25 (percent — the earlier builder)
 * both read 3.25. No CRE rate, floor or cap is 20% or more, and none is below
 * 0.2%, so the unit is unambiguous. "4.50%" strings are read as written.
 */
export function ratePercent(v: unknown): number | null {
  const s = str(v)
  if (s && /%$/.test(s)) {
    const n = parseFloat(s)
    return Number.isFinite(n) ? n : null
  }
  const n = num(v)
  if (n == null || n < 0) return null
  return n < 0.2 ? n * 100 : n
}

/** "350 bps" / "S+285" / 0.0295 (fraction) / 2.95 (percent) / "SOFR + 350 bps" → bps. */
export function spreadToBps(v: unknown): number | null {
  const n = num(v)
  if (n != null && n > 0) {
    if (n < 0.2) return Math.round(n * 10_000) // fraction: 0.0295
    if (n < 20) return Math.round(n * 100) // percent: 2.95
    return Math.round(n) // already bps
  }
  const s = str(v)
  if (!s) return null
  const bps = s.match(/(\d+(?:\.\d+)?)\s*(?:bps|bp)\b/i)
  if (bps) return Math.round(Number(bps[1]))
  const plus = s.match(/(?:S|SOFR|Term SOFR)\s*\+\s*(\d+(?:\.\d+)?)\s*(%)?/i)
  if (plus) {
    const x = Number(plus[1])
    return plus[2] ? Math.round(x * 100) : x < 20 ? Math.round(x * 100) : Math.round(x)
  }
  return null
}

function feePct(pctValue: unknown, bpsValue: unknown): string | null {
  const s = str(pctValue)
  if (s && /%$/.test(s) && Number.isFinite(parseFloat(s))) return pct(parseFloat(s))
  const bps = num(bpsValue)
  if (bps != null && bps >= 0) return pct(bps / 100)
  return null
}

function titleCaseRecourse(v: string): string {
  const lower = v.toLowerCase()
  if (lower.startsWith('non-recourse') || lower.startsWith('non recourse') || lower.includes('carve-out') || lower.includes('carveout')) {
    return 'Non-Recourse'
  }
  if (lower.startsWith('partial')) return 'Partial Recourse'
  if (lower.startsWith('full')) return 'Full Recourse'
  return v
}

/**
 * A custom field by its stored (snake_case) name. The loans service camel-cases
 * every nested key on the way in, so `term_months` arrives as `termMonths`;
 * both spellings are read.
 */
function field(cf: Record<string, unknown>, snake: string): unknown {
  if (snake in cf) return cf[snake]
  const camel = snake.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
  return cf[camel]
}

export const SECURITY_MEZZANINE = 'Pledge of 100% of the equity interests in the Mortgage Borrower'
export const SECURITY_SECOND_MORTGAGE = 'Second Mortgage'

/**
 * The purpose clause of the summary letter: "to finance the acquisition of
 * The Marlowe, a multifamily property located at 4400 Willowbend Drive, …".
 * The template read "to acquisition the The Marlowe" and "a office property".
 */
export function letterPurpose(transactionType: string, property: string, assetType: string, location: string): string {
  const t = transactionType.trim().toLowerCase()
  const verb = /^refinanc/.test(t) ? 'refinance' : `finance the ${t || 'acquisition'} of`
  const type = assetType.trim().toLowerCase()
  const article = /^[aeiou]/.test(type) ? 'an' : 'a'
  // A street address is "located at"; a city is "located in".
  const where = /^\d/.test(location.trim()) ? 'at' : 'in'
  return `to ${verb} ${property}, ${article} ${type} property located ${where} ${location}`
}

/** The terms the deal record states, in the builder's vocabulary. */
export function termsFromDeal(deal: TermSheetDealLike): DealTermPrefill {
  const raw = (deal.customFields && typeof deal.customFields === 'object' ? deal.customFields : {}) as Record<string, unknown>
  const cf = new Proxy(raw, { get: (target, key) => (typeof key === 'string' ? field(target, key) : undefined) })
  const out: DealTermPrefill = {}

  const amount = toMillions(deal.loanAmount, amountUnitOf(deal))
  if (amount > 0) out.loanAmount = +amount.toFixed(4)

  const purpose = str(cf.purpose)
  if (purpose) out.transactionType = purpose

  const termMonths = num(cf.term_months)
  if (termMonths != null && termMonths > 0) out.term = `${termMonths} Months`

  const spread = spreadToBps(cf.spread) ?? spreadToBps(deal.rate)
  if (spread != null) out.spreadBps = spread

  const index = str(cf.index) ?? (str(deal.rate)?.match(/\b(Term SOFR|SOFR)\b/i)?.[1] ?? null)
  const fixed = (index != null && /^fixed$/i.test(index)) || str(cf.rate_type)?.toLowerCase() === 'fixed'
  if (index) out.rateIndex = fixed ? 'Fixed (No Index)' : index
  if (fixed) {
    // A fixed-rate deal states its coupon ("6.35%"), not an index spread; the
    // letter read "Fixed (No Index) + 350 bps (1.00% floor)" and never 6.35%.
    out.rateIndex = 'Fixed (No Index)'
    const coupon = ratePercent(cf.rate) ?? ratePercent(cf.interest_rate) ?? ratePercent(deal.rate)
    if (coupon != null && coupon > 0) out.fixedRatePct = pct(coupon)
  }

  const floor = num(cf.floor)
  if (floor != null && floor >= 0) out.rateFloorPct = pct(ratePercent(floor) ?? floor)

  const amortType = str(cf.amortization_type)
  const amortMonths = num(cf.amortization_months)
  if (amortType && /interest only|^io\b/i.test(amortType)) {
    out.amortization = 'Interest Only'
    out.ioPeriod = 'Full Term'
  } else if (amortMonths === 0) {
    out.amortization = 'Interest Only'
  } else if (amortMonths === 300) {
    out.amortization = '25-Year'
  } else if (amortMonths === 360) {
    out.amortization = '30-Year'
  }
  const ioMonths = num(cf.io_months) ?? num(cf.io_period)
  if (ioMonths != null && ioMonths > 0) out.ioPeriod = `${ioMonths} Months`

  // A stated option string ("2 x 12 months") first; else the catalogue's
  // `extensions` field, which records the extension term in MONTHS.
  const extension = str(cf.extension_options)
  const extensionMonths = num(cf.extensions)
  if (extension) out.extension = extension
  else if (extensionMonths != null && extensionMonths > 0) out.extension = `${extensionMonths} months`

  const prepayment = str(cf.prepayment)
  if (prepayment) out.prepaymentFee = prepayment

  const orig = feePct(cf.origination_fee, cf.origination_fee_bps)
  if (orig) out.origFeePct = orig
  const exit = feePct(cf.exit_fee, cf.exit_fee_bps)
  if (exit) out.exitFeePct = exit

  const recourse = str(cf.recourse)
  if (recourse) out.recourse = titleCaseRecourse(recourse)

  // A subordinate position is secured by what the deal states it is. Stanhope
  // Row (P11, a mezzanine note, second position) was offered on "First
  // Mortgage Only".
  const position = str(cf.position) ?? ''
  const lien = str(cf.lien_position) ?? ''
  if (/mezz/i.test(position) || /mezz/i.test(lien)) out.security = SECURITY_MEZZANINE
  else if (/^(second|2nd|junior|subordinate)/i.test(lien) || /^(second|2nd)/i.test(position)) out.security = SECURITY_SECOND_MORTGAGE

  const broker = str(deal.brokerName) ?? str(cf.broker_name)
  if (broker) out.brokerName = broker
  const brokerFirm = str(cf.brokerage_company)
  if (brokerFirm) out.brokerFirm = brokerFirm
  const brokerPhone = str(cf.broker_phone)
  if (brokerPhone) out.brokerPhone = brokerPhone

  return out
}

/**
 * The draft fields a structure saved by the pre-fix builder holds in the wrong
 * unit (MOD-01): that builder stored the live loan amount — DOLLARS — under
 * `loanAmountM`, and every default it computed from it a million times too
 * large.
 */
export const LOAN_SCALED_DRAFT_KEYS = ['loanAmountM', 'goodFaithDeposit', 'netWorth', 'liquidity', 'earnoutAmount', 'minEquity'] as const

/**
 * The unit this builder declares on every draft it saves (`loanAmountM` in
 * $MM). A draft that carries it is read as written; only an undeclared draft
 * can be one the pre-fix builder wrote in dollars.
 */
export const DRAFT_LOAN_UNIT = 'USD_MM'

/** Every positive loan amount the deal's field history records (old and new
 *  values), distinct and ascending. */
export function recordedLoanAmounts(events: readonly { oldValue?: unknown; newValue?: unknown }[] | null | undefined): number[] {
  const out = new Set<number>()
  for (const event of events ?? []) {
    for (const raw of [event.oldValue, event.newValue]) {
      const n = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() ? Number(raw) : NaN
      if (Number.isFinite(n) && n > 0) out.add(n)
    }
  }
  return [...out].sort((a, b) => a - b)
}

/**
 * Whether a saved draft is one the pre-fix builder wrote in dollars-as-
 * millions. The evidence is the record, not the size: that builder copied the
 * deal's dollar loan amount verbatim into `loanAmountM`, so a draft whose
 * `loanAmountM` equals the deal's loan in dollars — now, or at any point its
 * field history records — is that error. Matching only the current loan let a
 * draft saved before the loan was changed print "$24000000.0M" (nd3-33). A
 * draft this builder saved declares its unit and is never read as that error.
 *
 * This replaces `repairMillions` / `repairDepositDollars`, which divided any
 * value at or above a ceiling by 1e6 — a unit decided by size, which the
 * single-amount rule forbids: a deposit of $2,000,000,000 read as $2K.
 * Nothing is rescaled now. The builder does not read the affected fields of
 * such a draft; it re-offers them from the deal record and its defaults, and
 * marks them Suggested until a person confirms them.
 */
export function isDollarsAsMillionsDraft(
  deal: TermSheetDealLike,
  extraTerms: Record<string, unknown> | null | undefined,
  recordedLoans: readonly number[] = [],
): boolean {
  const stored = extraTerms?.loanAmountM
  if (typeof stored !== 'number' || extraTerms?.loanAmountUnit === DRAFT_LOAN_UNIT) return false
  if (amountUnitOf(deal) !== 'USD') return false
  const current = Number.isFinite(deal.loanAmount) && deal.loanAmount > 0 && stored === deal.loanAmount
  return current || (stored > 0 && recordedLoans.includes(stored))
}

/**
 * The largest loan amount a `loanAmountM` ($MM) figure can be: $100 billion.
 * No commercial real-estate loan approaches it.
 */
export const MAX_POSSIBLE_LOAN_MM = 100_000

/**
 * Whether the builder must not read a saved draft's loan-scaled fields: a
 * dollars-as-millions draft by the record (`isDollarsAsMillionsDraft`), or an
 * undeclared draft whose `loanAmountM` no $MM figure can be. The round-2 probe
 * — 24,000,000 on a draft whose loan the deal never held — printed
 * "$24000000.0M" ($24 trillion) because the record held no matching amount
 * (nd3-33). Nothing is rescaled and no unit is inferred: an impossible figure
 * is simply not printed, and the deal's own figures are re-offered as
 * Suggestions, as for any draft the builder does not read. A draft this
 * builder saved declares its unit and is always read as written.
 */
export function isUnreadableLegacyDraft(
  deal: TermSheetDealLike,
  extraTerms: Record<string, unknown> | null | undefined,
  recordedLoans: readonly number[] = [],
): boolean {
  if (isDollarsAsMillionsDraft(deal, extraTerms, recordedLoans)) return true
  const stored = extraTerms?.loanAmountM
  if (typeof stored !== 'number' || extraTerms?.loanAmountUnit === DRAFT_LOAN_UNIT) return false
  return Number.isFinite(stored) && Math.abs(stored) > MAX_POSSIBLE_LOAN_MM
}
